import { useState } from "react";
import { CircleCheck, Loader2, Scale, SquarePen } from "lucide-react";
import {
  DECISION_SOURCE_LABELS,
  decisionState,
  type Decision,
} from "@gb/contracts";
import { ContentColumn } from "@/app/app-shell";
import { useProjectContext } from "@/app/project-context";
import { EmptyState } from "@/components/gb/empty-state";
import { Input } from "@/components/ui/input";
import { useUrlSearch } from "@/hooks/use-url-search";
import { formatDateShort } from "@/lib/format";
import { cn } from "@/lib/utils";
import { BrainTabs } from "./brain-tabs";
import { DecisionDialog } from "./decision-dialog";
import { useDecisions } from "./decisions-api";

/**
 * What this project committed to, and why.
 *
 * A grid rather than the list the Sources tab uses, and the difference is the
 * content: a source is a thing you open, so a row with a title and a citation is
 * enough. A decision is a thing you *read* — the summary is the whole point of
 * it, and a row that truncated it would send every reader into a detail screen
 * to learn what a two-line card could have told them.
 *
 * There is no detail screen, and that is why this shows the whole summary.
 */
export function DecisionsScreen() {
  const { projectId } = useProjectContext();
  // Mirrored into `?q=` like the Brain's own search, so a filtered view is a
  // link somebody can send.
  const { term, setTerm, debounced } = useUrlSearch("q");
  // Held by id rather than by value, so the open dialog follows a refetch
  // instead of showing the copy it was opened with.
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isPending, isError } = useDecisions(projectId, debounced.trim());
  const open = openId ? (data?.decisions.find((d) => d.id === openId) ?? null) : null;

  return (
    <ContentColumn tight>
      <BrainTabs />

      {isError ? (
        <p role="alert" className="text-[0.92rem] font-semibold text-danger">
          Those decisions could not be loaded.
        </p>
      ) : isPending ? (
        <div className="flex items-center gap-2 py-16 text-muted" role="status">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-[0.9rem]">Loading decisions…</span>
        </div>
      ) : data.total === 0 ? (
        // Nothing has been decided, which is not the same as nothing matching.
        <EmptyState
          icon={Scale}
          title="No decisions yet"
          description="Decisions arrive two ways: captured from email, WhatsApp and site journals, or by accepting an answer on the Ask tab."
        />
      ) : (
        <>
          <div className="mb-4">
            <Input
              value={term}
              placeholder="Search decisions, vendors, tags"
              aria-label="Search decisions"
              className="max-w-[420px]"
              onChange={(event) => setTerm(event.target.value)}
            />
            <p className="mt-2 text-[0.82rem] text-muted">
              {/* Counts the whole set when nothing is typed and the matches when
                  something is, because those answer different questions — "how
                  much is here" and "how much of it is about this". */}
              {debounced.trim()
                ? `${data.decisions.length} of ${data.total} match`
                : `${data.total} ${data.total === 1 ? "decision" : "decisions"} captured from email, WhatsApp & site journals`}
            </p>
          </div>

          {data.decisions.length === 0 ? (
            // Distinct from the empty state above: there *are* decisions, none
            // of them match. Telling a reader "no decisions yet" here would be
            // a false statement about the project rather than about the filter.
            <p className="py-12 text-center text-[0.9rem] text-muted">
              Nothing matches “{debounced.trim()}”.
            </p>
          ) : (
            <div className="grid gap-3.5 pb-8 md:grid-cols-2">
              {data.decisions.map((decision) => (
                <DecisionCard
                  key={decision.id}
                  decision={decision}
                  onOpen={() => setOpenId(decision.id)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {open && <DecisionDialog projectId={projectId} decision={open} onClose={() => setOpenId(null)} />}
    </ContentColumn>
  );
}

function DecisionCard({ decision, onOpen }: { decision: Decision; onOpen: () => void }) {
  const state = decisionState(decision);
  const changed = state === "accepted-with-changes";

  return (
    // A button, not a card with a link in it: the whole card is the target, and
    // the summary inside it is prose rather than a label — wrapping prose in an
    // anchor gives a screen reader a link named by three sentences.
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open decision: ${decision.title}`}
      className="flex flex-col rounded-card-lg border-[1.5px] border-border bg-surface px-5 py-4 text-left transition-colors hover:border-brand/50">
      <div className="mb-2 flex items-center gap-2">
        <span className="font-display text-[0.62rem] font-semibold tracking-[0.1em] text-muted uppercase">
          {DECISION_SOURCE_LABELS[decision.source]}
        </span>
        <span className="ml-auto text-[0.8rem] text-muted">
          {formatDateShort(decision.date)}
        </span>
      </div>

      <h3 className="mb-2 font-display text-[1rem] leading-snug font-semibold text-ink">
        {decision.title}
      </h3>
      <p className="mb-3 text-[0.88rem] leading-relaxed text-ink/80">{decision.summary}</p>

      {decision.modificationNote && (
        // **Shown, and shown as a change.** The statement above is drafted
        // with this change (`data-model/0014` §7); this is where a person
        // disagreed with the answer, in their own words. Rendering only one of
        // them — either one — throws away the pair that makes the record worth
        // keeping. See `data-model/0009` §2.1.
        <p className="mb-3 rounded-input border-l-[3px] border-warn/60 bg-warn-bg/60 px-3 py-2 text-[0.84rem] leading-relaxed text-ink">
          <span className="font-display text-[0.62rem] font-semibold tracking-[0.1em] text-warn uppercase">
            Modified on acceptance
          </span>
          <span className="mt-1 block">{decision.modificationNote}</span>
        </p>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
        {decision.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full bg-subtle px-2.5 py-[3px] text-[0.74rem] text-muted"
          >
            {tag}
          </span>
        ))}
        <span
          className={cn(
            "ml-auto flex items-center gap-1.5 rounded-full border px-2.5 py-[3px]",
            "font-display text-[0.66rem] font-semibold tracking-[0.06em] uppercase",
            changed
              ? "border-warn/40 bg-warn-bg text-warn"
              : "border-ok/40 bg-ok-bg text-ok",
          )}
        >
          {changed ? <SquarePen size={12} aria-hidden /> : <CircleCheck size={12} aria-hidden />}
          {changed ? "Accepted with changes" : "Accepted"}
        </span>
      </div>
    </button>
  );
}
